import {
  FORMULA_FUNCTION_ARITY,
  FormulaBinaryOp,
  FormulaFunctionName,
  FormulaNode,
} from "../../types/recommendationEngine";

export type FormulaTerm = {
  value: number;
  signalId: string;
  // Optional single-argument math wrapper applied to the signal before multiplying.
  functionName?: FormulaFunctionName;
};

/** Math functions that take exactly one argument — the only ones the Term List supports. */
export const SINGLE_ARG_FUNCTION_NAMES: FormulaFunctionName[] = [
  "abs",
  "sqrt",
  "log",
  "log10",
  "exp",
  "floor",
  "ceil",
  "round",
];

const buildSignalNode = (term: FormulaTerm): FormulaNode => {
  const base: FormulaNode = { type: "signal", signalId: term.signalId };
  if (!term.functionName) {
    return base;
  }
  return { type: "function", name: term.functionName, args: [base] };
};

const buildWeightedTerm = (term: FormulaTerm): FormulaNode => ({
  type: "binary",
  op: "*",
  left: { type: "literal", value: term.value },
  right: buildSignalNode(term),
});

export function termListToAst(
  op: FormulaBinaryOp,
  terms: FormulaTerm[],
): FormulaNode {
  if (terms.length === 0) {
    return { type: "literal", value: 0 };
  }
  if (terms.length === 1) {
    return buildWeightedTerm(terms[0]);
  }
  return terms.slice(1).reduce<FormulaNode>(
    (acc, t) => ({
      type: "binary",
      op,
      left: acc,
      right: buildWeightedTerm(t),
    }),
    buildWeightedTerm(terms[0]),
  );
}

const matchSignalOrWrappedSignal = (
  node: FormulaNode,
): { signalId: string; functionName?: FormulaFunctionName } | null => {
  if (node.type === "signal") {
    return { signalId: node.signalId };
  }
  if (
    node.type === "function" &&
    node.args.length === 1 &&
    node.args[0].type === "signal" &&
    SINGLE_ARG_FUNCTION_NAMES.includes(node.name)
  ) {
    return { signalId: node.args[0].signalId, functionName: node.name };
  }
  return null;
};

function isLiteralSignalProduct(node: FormulaNode): FormulaTerm | null {
  const sigMatch = matchSignalOrWrappedSignal(node);
  if (sigMatch) {
    return { value: 1, ...sigMatch };
  }
  if (node.type === "binary" && node.op === "*") {
    if (node.left.type === "literal") {
      const right = matchSignalOrWrappedSignal(node.right);
      if (right) {
        return { value: node.left.value, ...right };
      }
    }
    if (node.right.type === "literal") {
      const left = matchSignalOrWrappedSignal(node.left);
      if (left) {
        return { value: node.right.value, ...left };
      }
    }
  }
  return null;
}

export function astToTermList(
  node: FormulaNode,
): { op: FormulaBinaryOp; terms: FormulaTerm[] } | null {
  const single = isLiteralSignalProduct(node);
  if (single) {
    return { op: "+", terms: [single] };
  }

  if (node.type !== "binary" || !["+", "-", "*", "/"].includes(node.op)) {
    return null;
  }

  const op = node.op;
  const collect = (n: FormulaNode): FormulaTerm[] | null => {
    const product = isLiteralSignalProduct(n);
    if (product) {
      return [product];
    }
    if (n.type === "binary" && n.op === op) {
      const left = collect(n.left);
      const right = collect(n.right);
      if (left && right) {
        return [...left, ...right];
      }
    }
    return null;
  };

  const terms = collect(node);
  if (!terms || terms.length === 0) {
    return null;
  }
  return { op, terms };
}

export function astToDisplayString(node: FormulaNode): string {
  if (node.type === "literal") {
    return String(node.value);
  }
  if (node.type === "signal") {
    return node.signalId;
  }
  if (node.type === "function") {
    const args = node.args.map(astToDisplayString).join(", ");
    return `${node.name}(${args})`;
  }
  const l = astToDisplayString(node.left);
  const r = astToDisplayString(node.right);
  const sym = { "+": "+", "-": "−", "*": "×", "/": "÷" }[node.op];
  return `(${l} ${sym} ${r})`;
}

export function createDefaultFormulaNode(): FormulaNode {
  return { type: "literal", value: 0 };
}

export function createDefaultFunctionNode(
  name: FormulaFunctionName,
): FormulaNode {
  const arity = FORMULA_FUNCTION_ARITY[name];
  const argCount = Number.isFinite(arity.min) ? arity.min : 1;
  return {
    type: "function",
    name,
    args: Array.from({ length: argCount }, () => createDefaultFormulaNode()),
  };
}

export function walkFormulaSignals(
  node: FormulaNode,
  acc: Set<string> = new Set(),
): Set<string> {
  if (node.type === "signal") {
    acc.add(node.signalId);
    return acc;
  }
  if (node.type === "binary") {
    walkFormulaSignals(node.left, acc);
    walkFormulaSignals(node.right, acc);
    return acc;
  }
  if (node.type === "function") {
    node.args.forEach((arg) => walkFormulaSignals(arg, acc));
  }
  return acc;
}
